/**
 * SessionCoordinator - Manages session state and presence reporting
 *
 * Responsibilities:
 * - Track session presence (status, player count, peer IDs)
 * - Track running game (temporary - will move to dedicated message in Phase 2)
 * - Calculate static host properties on init
 * - Send presence updates to realtime API
 *
 * Events:
 * - presenceUpdated: When presence state changes
 */

import type { StateStore, HostBroadcastStatus } from '@playcastdotio/CoreTypes';
import { captureStopDecision, pipelineSettledAtMs } from '@playcastdotio/PeerCore';
import type { StaticHostProperties, SessionPresenceState, HostBroadcastState } from './stores';
import { createSessionStores } from './stores';
import { PresenceManager } from './managers/PresenceManager';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import type { IPeerManager } from '../../services/peer/IPeerManager';
import type { GameLaunchCoordinator} from '../game-launch/GameLaunchCoordinator';
import type { PeerCoordinator } from '../peer/PeerCoordinator';
import type { AppCoordinator } from '../app/AppCoordinator';
import { websocket, PeerMessageType, PeerIdType, MatchmakingMessageType } from '@playcastdotio/Messaging';
import { CoordinatorEventImpl } from '../_types/events';
import type { IHostAnalyticsManager } from '../../services/analytics/IHostAnalyticsManager';

// ========================================
// SessionCoordinator
// ========================================

/**
 * SessionCoordinator
 */
export class SessionCoordinator {
  private presenceStore: StateStore<SessionPresenceState>;
  private staticPropertiesStore: StateStore<StaticHostProperties>;
  private broadcastStore: StateStore<HostBroadcastState>;
  private realtimeService: IRealtimeService;
  private peerManager: IPeerManager;
  private analytics: IHostAnalyticsManager;
  private peerCoordinator?: PeerCoordinator; // Store reference for peer ID events
  private isStreamStarting = false; // Guard to prevent duplicate stream start
  /** Single-flight for startStream — concurrent ADMIN_SESSION_PREPARE must not double PEER_GET_ID. */
  private streamStartPromise: Promise<void> | null = null;
  private currentInvitationId?: string; // Store invitation ID for MATCHMAKER_HOST_READY
  private currentCampaignId?: string; // Store campaign ID for analytics correlation
  private peerConnectionTimeout: ReturnType<typeof setTimeout> | null = null; // 30s timeout for peer connection
  private unregisterHostAuthSuccess: (() => void) | null = null;

  // Session timing for analytics
  private streamStartedAt = 0;   // When startStream() was called
  private sessionStartedAt = 0;  // When first player connected
  private hasActiveSession = false;   // True while a player is connected
  /** True while a player may connect before the next match offer (Pavilion pool). */
  private hostJoinable = false;
  /** When capture warmth was armed. Null after a hard tear-down. */
  private warmArmedAtMs: number | null = null;
  /** Set in init(); used by onHostAuthenticated (same instance as initializeCoordinators, not a separate module singleton). */
  private appCoordinatorRef?: AppCoordinator;

  private managers: {
    presence: PresenceManager;
  };

  public events = {
    presenceUpdated: new CoordinatorEventImpl<void>(),
    streamStarted: new CoordinatorEventImpl<{ hostPeerId: string; timestamp: number }>(),
    streamStopped: new CoordinatorEventImpl<{ reason?: string; timestamp: number; retainCapture: boolean }>(),
    warmthArmed: new CoordinatorEventImpl<{ timestamp: number }>(),
    joinableChanged: new CoordinatorEventImpl<{ joinable: boolean; timestamp: number }>(),
    /** Fired when the host leaves the pool and any retained capture must stop. */
    captureReleaseRequested: new CoordinatorEventImpl<{ timestamp: number }>(),
    peerConnectionTimeout: new CoordinatorEventImpl<{ invitationId?: string; timestamp: number }>(),
    allPlayersLeft: new CoordinatorEventImpl<{ timestamp: number }>(),
    statusChanged: new CoordinatorEventImpl<{ status: 'offline' | 'idle' | 'busy' | 'hosting'; timestamp: number }>(),
    broadcastStatusChanged: new CoordinatorEventImpl<{ status: HostBroadcastStatus; timestamp: number }>(),
  };

  constructor(realtimeService: IRealtimeService, peerManager: IPeerManager, analytics: IHostAnalyticsManager) {
    this.realtimeService = realtimeService;
    this.peerManager = peerManager;
    this.analytics = analytics;

    const stores = createSessionStores();
    this.presenceStore = stores.presence;
    this.staticPropertiesStore = stores.staticProperties;
    this.broadcastStore = stores.broadcast;

    this.managers = {
      presence: new PresenceManager(stores, realtimeService),
    };
  }

  /**
   * Initialize the coordinator
   *
   * @param appCoordinator - AppCoordinator to read static properties from
   * @param gameLaunchCoordinator - GameLaunchCoordinator to listen to for runningGame updates
   * @param peerCoordinator - PeerCoordinator to listen to for player count updates
   */
  public async init(
    appCoordinator: AppCoordinator,
    gameLaunchCoordinator?: GameLaunchCoordinator,
    peerCoordinator?: PeerCoordinator
  ): Promise<void> {
    console.log('[SessionCoordinator] Initializing...');

    this.appCoordinatorRef = appCoordinator;

    // Store peer coordinator reference for peer ID events during stream start
    if (peerCoordinator) {
      this.peerCoordinator = peerCoordinator;
    }

    // Read static properties from AppCoordinator (single source of truth)
    const staticProps = appCoordinator.getStaticProperties();
    this.staticPropertiesStore.updateState(staticProps);
    console.log('[SessionCoordinator] ✓ Static properties loaded from AppCoordinator:', staticProps);

    // Initialize managers
    this.managers.presence.init();

    // Set up peer manager event listeners
    this.wirePeerManagerEvents();

    // Listen to GameLaunchCoordinator for runningGame updates (temporary)
    if (gameLaunchCoordinator) {
      this.wireGameLaunchEvents(gameLaunchCoordinator);
    }

    // Listen to PeerCoordinator for player count updates
    if (peerCoordinator) {
      this.wirePeerEvents(peerCoordinator);
    }

    this.unregisterHostAuthSuccess?.();
    this.unregisterHostAuthSuccess = this.realtimeService.onHostAuthSuccess(() => {
      this.onHostAuthenticated();
    });

    console.log('[SessionCoordinator] ✓ Initialized');
  }

  /**
   * Call after AUTH_SUCCESS so static props (e.g. isCloudHost) match AppCoordinator
   * and realtime-api receives a fresh PRESENCE_UPDATE. Guest-session reconnects after
   * nonsole kill switch otherwise could leave stale static fields or miss a timely update.
   */
  public onHostAuthenticated(): void {
    const coord = this.appCoordinatorRef;
    if (!coord) {
      console.error('[SessionCoordinator] onHostAuthenticated called before init');
      return;
    }
    const fresh = coord.getStaticProperties();
    this.staticPropertiesStore.updateState(fresh);
    this.managers.presence.flushPresenceToApi();
  }

  /**
   * Wire GameLaunchCoordinator events for runningGame updates
   */
  private wireGameLaunchEvents(gameLaunchCoordinator: GameLaunchCoordinator): void {
    console.log('[SessionCoordinator] Wiring GameLaunchCoordinator events...');

    // Listen to campaign switch success (game launched)
    gameLaunchCoordinator.events.campaignSwitchSucceeded.addListener((data) => {
      console.log('[SessionCoordinator] Game launched, updating presence');

      // Get game info from GameLaunchCoordinator
      // GameLaunchCoordinator doesn't expose getState(), so we'll set a placeholder
      // TODO: Get actual game info from GameLaunchCoordinator public API
      // For now, we know a game launched but don't have the details yet
      console.warn('[SessionCoordinator] Game launched but details not available from GameLaunchCoordinator API');
      // this.updateRunningGame({ gameId, gameName }); // Will implement when API exists
    });

    // Listen to game stopped (clear runningGame)
    gameLaunchCoordinator.events.campaignSwitchFailed.addListener(() => {
      console.log('[SessionCoordinator] Game stopped (failed), clearing presence');
      this.updateRunningGame(undefined);
      this.leaveJoinable();
    });

    // Listen to game closed (stop stream)
    gameLaunchCoordinator.events.gameClosed.addListener((data) => {
      console.log('[SessionCoordinator] Game closed, stopping stream:', data.gameName);
      this.updateRunningGame(undefined);
      // A joinable host is restarting the game for the next player. Keep capture.
      // A real exit clears joinable afterwards and requests capture release.
      if (this.hostJoinable) {
        this.stopStream('host:game_closed_while_joinable');
        return;
      }
      this.stopStream('host:game_closed');
    });
  }

  /**
   * Wire PeerCoordinator events
   */
  private wirePeerEvents(peerCoordinator: PeerCoordinator): void {
    console.log('[SessionCoordinator] Wiring PeerCoordinator events...');

    // Listen for player count changes
    peerCoordinator.events.playerCountChanged.addListener((count) => {
      console.log(`[SessionCoordinator] Player count changed: ${count}`);
      this.updatePlayerCount(count);
    });

    // Listen for peer ID changes
    peerCoordinator.events.hostPeerIdChanged.addListener((peerId) => {
      console.log(`[SessionCoordinator] Host peer ID changed: ${peerId}`);
      this.updateHostPeerId(peerId);
    });

    peerCoordinator.events.remoteHostPeerIdChanged.addListener((peerId) => {
      console.log(`[SessionCoordinator] Remote host peer ID changed: ${peerId}`);
      this.updateRemoteHostPeerId(peerId);
    });

    // Optional: Listen for individual player events for logging/analytics
    peerCoordinator.events.onPlayerJoined.addListener(({ peerId }) => {
      console.log(`[SessionCoordinator] Player joined: ${peerId}`);
      this.clearPeerConnectionTimeout();
      if (!this.hasActiveSession) {
        this.hasActiveSession = true;
        this.sessionStartedAt = Date.now();
        this.analytics.sendSessionStartedEvent({
          invitationId: this.currentInvitationId,
          campaignId: this.currentCampaignId,
        });
      }
    });

    peerCoordinator.events.onPlayerLeft.addListener(({ peerId }) => {
      console.log(`[SessionCoordinator] Player left: ${peerId}`);
    });

    // Detect when all players have left while hosting
    peerCoordinator.events.playerCountChanged.addListener((newCount) => {
      if (newCount === 0 && this.presenceStore.currentState.status === 'hosting') {
        console.log('[SessionCoordinator] 🚪 All players left while hosting');
        this.events.allPlayersLeft.emit({ timestamp: Date.now() });
      }
    });
  }

  /**
   * Wire PeerManager events for WebRTC peer status changes
   */
  private wirePeerManagerEvents(): void {
    console.log('[SessionCoordinator] Wiring PeerManager events...');

    // Listen to peer status changes
    this.peerManager.onStatusChange(async (status) => {
      console.log('[SessionCoordinator] Peer status changed:', status.connectionState);

      if (status.connectionState === 'listening') {
        // Guard against duplicate stream start (can happen if status callback fires multiple times)
        const currentStatus = this.presenceStore.currentState.status;
        if (currentStatus === 'hosting' || this.isStreamStarting) {
          console.log('[SessionCoordinator] Stream already started or starting, ignoring duplicate status change');
          return;
        }
        this.isStreamStarting = true;

        try {
          // Peer is ready to accept connections
          console.log('[SessionCoordinator] Peer listening, stream is ready');

          // Check if we have an invitation ID (should have been passed to startStream())
          if (!this.currentInvitationId) {
            console.warn('[SessionCoordinator] ⚠️  No invitation ID available, cannot send MATCHMAKER_HOST_READY');
          }

          // Now the stream is ready
          console.log('[SessionCoordinator] Stream is live');
          this.updateStatus('hosting');

          // Send MATCHMAKER_HOST_READY to API (tells API we're ready for clients to join)
          // Only send if we have an invitation ID
          if (this.currentInvitationId) {
            await this.sendHostReady(this.currentInvitationId);
            this.analytics.sendReadyForPlayerEvent({
              invitationId: this.currentInvitationId,
            });
          }

          // Emit streamStarted event with host peer ID
          const hostPeerId = this.presenceStore.currentState.hostPeerId;
          if (hostPeerId) {
            this.noteWarmArmed(Date.now());
            this.events.streamStarted.emit({
              hostPeerId,
              timestamp: Date.now(),
            });
          }
        } catch (error) {
          console.error('[SessionCoordinator] Failed to start stream:', error);
          this.updateStatus('idle');
        } finally {
          // Reset guard after completion (allow restart if stream stops and starts again)
          this.isStreamStarting = false;
        }
      } else if (['closed', 'failed', 'disconnected'].includes(status.connectionState)) {
        // Peer failed or disconnected → stream stopped
        console.error('[SessionCoordinator] Peer connection failed or closed:', status.connectionState);
        this.updateStatus('idle');

        // Clear peer connection timeout (prevents redundant timeout firing after peer already closed)
        this.clearPeerConnectionTimeout();

        this.emitStreamStopped(status.connectionState);
      }
    });

    // Listen to remote peer connections/disconnections
    this.peerManager.onRemotePeerChange((event) => {
      console.log('[SessionCoordinator] Remote peer change:', event);
      // PeerCoordinator handles player tracking, we just log here
    });
  }

  /**
   * Send MATCHMAKER_HOST_READY message to API
   *
   * This tells the API that the host is ready for clients to join.
   * Must be called AFTER:
   * - Peer is listening
   * - Invitation ID is available
   * - Host peer ID is available
   *
   * TODO-STRANGLER-FIG: This should eventually live in PavilionCoordinator
   * (matchmaking domain), not SessionCoordinator (presence domain).
   */
  private async sendHostReady(invitationId: string | undefined): Promise<void> {
    const hostPeerId = this.presenceStore.currentState.hostPeerId;

    if (!hostPeerId) {
      console.error('[SessionCoordinator] ❌ Cannot send HOST_READY: Host peer ID not set');
      return;
    }

    if (!invitationId) {
      console.error('[SessionCoordinator] ❌ Cannot send HOST_READY: Invitation ID not set');
      return;
    }

    console.log('[SessionCoordinator] 📤 Sending MATCHMAKER_HOST_READY:', {
      invitationId,
      hostPeerId,
    });

    const message = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_HOST_READY, {
      invitationId,
      hostPeerId,
    });

    try {
      this.realtimeService.sendMessage(message);
      console.log('[SessionCoordinator] ✅ MATCHMAKER_HOST_READY sent');

      // Start 30-second peer connection timeout
      this.startPeerConnectionTimeout(invitationId);
    } catch (error) {
      console.error('[SessionCoordinator] ❌ Failed to send MATCHMAKER_HOST_READY:', error);
      throw error;
    }
  }

  /**
   * Start 30-second peer connection timeout
   *
   * If no peer connects within 30 seconds after MATCHMAKER_HOST_READY is sent,
   * the host should re-enter the matchmaking pool with the running game.
   *
   * CRITICAL: When the timeout fires, we must clean up the stale WebRTC peer
   * and reset session status to 'idle'. Without this, startStream() will refuse
   * new streams because the host is still in 'hosting' state with a dead peer.
   */
  private startPeerConnectionTimeout(invitationId?: string): void {
    // Clear any existing timeout
    this.clearPeerConnectionTimeout();

    console.log('[SessionCoordinator] ⏱️  Starting 30s peer connection timeout');

    this.peerConnectionTimeout = setTimeout(() => {
      console.error('[SessionCoordinator] ⏰ Peer connection TIMEOUT (30s) - no client connected');
      this.peerConnectionTimeout = null;

      // Clean up stale stream state (close WebRTC peer, set status to idle)
      // Use resetStreamState() instead of stopStream() to avoid emitting streamStopped,
      // since peerConnectionTimeout is a more specific event for this scenario.
      this.resetStreamState('host:peer_connection_timeout');

      this.analytics.sendPeerConnectionTimeoutEvent({
        invitationId: this.currentInvitationId,
        campaignId: this.currentCampaignId,
      });

      // Emit timeout event for PavilionCoordinator to handle re-registration
      this.events.peerConnectionTimeout.emit({
        invitationId,
        timestamp: Date.now(),
      });
    }, 30000); // 30 seconds
  }

  /**
   * Clear peer connection timeout
   */
  private clearPeerConnectionTimeout(): void {
    if (this.peerConnectionTimeout) {
      clearTimeout(this.peerConnectionTimeout);
      this.peerConnectionTimeout = null;
      console.log('[SessionCoordinator] ✅ Peer connection timeout cleared');
    }
  }

  // ========================================
  // Public API - Getters (READ)
  // ========================================

  /**
   * Get current session status
   */
  public getStatus(): 'offline' | 'idle' | 'busy' | 'hosting' {
    return this.presenceStore.currentState.status;
  }

  /**
   * Get current player count
   */
  public getPlayerCount(): number {
    return this.presenceStore.currentState.playerCount;
  }

  /**
   * Get current running game
   */
  public getRunningGame(): { type: 'steam' | 'epic' | 'microsoft' | 'exe'; id: string } | undefined {
    return this.presenceStore.currentState.runningGame;
  }

  /**
   * Get static properties
   */
  public getStaticProperties(): StaticHostProperties {
    return this.staticPropertiesStore.currentState;
  }

  // ========================================
  // Public API - Actions (WRITE)
  // ========================================

  /**
   * Update session status
   */
  public updateStatus(status: 'offline' | 'idle' | 'busy' | 'hosting'): void {
    console.log('[SessionCoordinator] Updating status:', status);
    this.presenceStore.updateState({
      ...this.presenceStore.currentState,
      status,
      lastUpdated: Date.now(),
    });

    this.events.statusChanged.emit({ status, timestamp: Date.now() });

    // Map session status → broadcast status and replicate to node-service
    const broadcastStatusMap: Record<string, HostBroadcastStatus> = {
      hosting: 'streaming',
      busy: 'starting',
      idle: 'idle',
      offline: 'idle',
    };
    const nextBroadcastStatus = broadcastStatusMap[status] ?? 'idle';
    const broadcastStatusChanged = this.broadcastStore.currentState.status !== nextBroadcastStatus;
    this.broadcastStore.updateState({
      status: nextBroadcastStatus,
    });

    // Playjector hooks its per-device input flush into broadcast-state transitions
    // (see RemoteIO::broadcastStateChanged). Emitting on true transitions only
    // keeps the downstream IPC bridge from spamming duplicate status messages.
    if (broadcastStatusChanged) {
      this.events.broadcastStatusChanged.emit({
        status: nextBroadcastStatus,
        timestamp: Date.now(),
      });
    }

    this.events.presenceUpdated.emit();
  }

  /**
   * Update player count
   */
  public updatePlayerCount(playerCount: number): void {
    if (playerCount < 0) {
      console.error('[SessionCoordinator] Invalid player count:', playerCount);
      return;
    }

    console.log('[SessionCoordinator] Updating player count:', playerCount);
    this.presenceStore.updateState({
      ...this.presenceStore.currentState,
      playerCount,
      lastUpdated: Date.now(),
    });
    this.events.presenceUpdated.emit();
  }

  /**
   * Update host peer ID
   */
  public updateHostPeerId(hostPeerId: string | undefined): void {
    console.log('[SessionCoordinator] Updating host peer ID:', hostPeerId);
    this.presenceStore.updateState({
      ...this.presenceStore.currentState,
      hostPeerId,
      lastUpdated: Date.now(),
    });
    this.events.presenceUpdated.emit();
  }

  /**
   * Update remote host peer ID
   */
  public updateRemoteHostPeerId(remoteHostPeerId: string | undefined): void {
    console.log('[SessionCoordinator] Updating remote host peer ID:', remoteHostPeerId);
    this.presenceStore.updateState({
      ...this.presenceStore.currentState,
      remoteHostPeerId,
      lastUpdated: Date.now(),
    });
    this.events.presenceUpdated.emit();
  }

  /**
   * Update running game (temporary - Phase 2 will remove this)
   */
  public updateRunningGame(runningGame: { type: 'steam' | 'epic' | 'microsoft' | 'exe'; id: string } | undefined): void {
    console.log('[SessionCoordinator] Updating running game:', runningGame?.id);
    this.presenceStore.updateState({
      ...this.presenceStore.currentState,
      runningGame,
      lastUpdated: Date.now(),
    });
    this.events.presenceUpdated.emit();
  }

  // ========================================
  // Stream Lifecycle Management
  // ========================================

  /**
   * Start the stream
   *
   * Orchestrates the stream start process:
   * 1. Close any existing peer
   * 2. Request peer ID from realtime API
   * 3. Wait for peer ID response (via PeerCoordinator event)
   * 4. Setup WebRTC peer via IPeerManager
   * 5. Wait for peer to reach 'listening' status
   * 6. Update presence status to 'hosting' and emit event
   *
   * @param invitationId - The invitation/match ID from matchmaking (passed by PavilionCoordinator)
   */
  public async startStream(invitationId?: string, campaignId?: string): Promise<void> {
    console.log('[SessionCoordinator] Starting stream for invitation:', invitationId || 'none');

    // Concurrent ADMIN_SESSION_PREPARE (or duplicate start) while PEER_GET_ID is in flight:
    // status is busy and hostPeerId is still absent. Reuse the pending start — do not reset
    // status or issue a second PEER_GET_ID (both waits would accept the same assignment and
    // double setupHostPeer).
    if (this.streamStartPromise) {
      if (invitationId) this.currentInvitationId = invitationId;
      if (campaignId) this.currentCampaignId = campaignId;
      console.warn('[SessionCoordinator] Stream start already in flight — reusing pending start');
      return this.streamStartPromise;
    }

    // Store invitation ID for later use (when sending MATCHMAKER_HOST_READY)
    this.currentInvitationId = invitationId;
    this.currentCampaignId = campaignId;

    // Check if already streaming. Admin PREPARE polls presence.hostPeerId — a bare
    // "Already streaming" return with no peer id makes realtime-api time out and deny
    // the session as machine_offline. Re-create the broadcast peer when missing and no
    // start is in flight (stuck hosting/busy after a failed or abandoned start).
    const currentStatus = this.presenceStore.currentState.status;
    if (currentStatus === 'hosting' || currentStatus === 'busy') {
      const existingPeerId = this.presenceStore.currentState.hostPeerId;
      if (existingPeerId) {
        console.warn(
          '[SessionCoordinator] Already streaming (status:',
          currentStatus,
          ', hostPeerId:',
          existingPeerId,
          ')',
        );
        return;
      }
      console.warn(
        '[SessionCoordinator] Already streaming without hostPeerId (status:',
        currentStatus,
        ') — recreating broadcast peer for Admin PREPARE',
      );
      this.updateStatus('idle');
    }

    const startPromise = this.executeStreamStart(invitationId, campaignId);
    this.streamStartPromise = startPromise.finally(() => {
      this.streamStartPromise = null;
    });
    return this.streamStartPromise;
  }

  private async executeStreamStart(invitationId?: string, campaignId?: string): Promise<void> {
    // Update status to busy (stream is starting)
    this.updateStatus('busy');

    // Record stream start time for analytics
    this.streamStartedAt = Date.now();

    this.analytics.sendStreamStartedEvent({
      invitationId: invitationId,
      campaignId,
    });

    try {
      // Step 1: Close existing peer if present
      const existingPeer = this.peerManager.getHostPeer();
      if (existingPeer) {
        console.log('[SessionCoordinator] Closing existing peer before creating new one');
        this.peerManager.closeHostPeer('host:duplicate_peer');
      }

      // Step 2: Request peer ID from realtime API
      console.log('[SessionCoordinator] Requesting peer ID from API...');
      this.realtimeService.sendMessage(
        websocket.generators.peer(PeerMessageType.PEER_GET_ID, {
          peerIdType: PeerIdType.HOST,
          peerMetadata: { signalingGenerationVersion: 1 },
        })
      );

      // Step 3: Wait for peer ID response (timeout after 10 seconds)
      const hostPeerId = await this.waitForPeerId(10000);
      console.log('[SessionCoordinator] Received peer ID:', hostPeerId);

      // CRITICAL: Update presence store with peer ID immediately
      // This ensures it's available when we send MATCHMAKER_HOST_READY
      this.updateHostPeerId(hostPeerId);

      // Step 4: Setup WebRTC peer
      console.log('[SessionCoordinator] Setting up WebRTC peer...');
      this.peerManager.setupHostPeer(hostPeerId);

      // Step 5: Wait for peer to reach 'listening' status (peer manager handles this internally)
      // The peer status listener will update our status to 'hosting' when ready

      console.log('[SessionCoordinator] ✅ Stream started successfully');
    } catch (error) {
      console.error('[SessionCoordinator] ❌ Failed to start stream:', error);
      // On error, set status back to idle
      this.updateStatus('idle');
      throw error;
    }
  }

  /**
   * Stop the stream
   *
   * Closes the WebRTC peer connection and updates presence to idle.
   * This should be called when:
   * - Game closes
   * - User manually stops streaming
   * - Host needs to disconnect
   *
   * Capture is retained while the host remains joinable unless reason is a hard stop.
   *
   * @param reason - Stop reason used for analytics and capture retention; see captureStopDecision.
   */
  public stopStream(reason?: string): void {
    console.log('[SessionCoordinator] Stopping stream:', reason || 'no reason');

    // Clear peer connection timeout
    this.clearPeerConnectionTimeout();

    // Emit session ended analytics if a player was connected during this stream
    if (this.hasActiveSession) {
      this.analytics.sendSessionEndedEvent({
        invitationId: this.currentInvitationId,
        campaignId: this.currentCampaignId,
        reason,
        sessionDurationMs: Date.now() - this.sessionStartedAt,
      });
    }

    // Clean up stream state (close peer, set idle, clear IDs)
    this.resetStreamState(reason);

    this.emitStreamStopped(reason);

    console.log('[SessionCoordinator] ✅ Stream stopped');
  }

  /**
   * Pavilion calls this when the machine is waiting for a player, before the
   * match offer. Emits warmthArmed once per armed period to request capture, and
   * joinableChanged on every call. A later call does not reset the settle clock.
   * nowMs is a Date.now()-domain timestamp; returning does not confirm capture readiness.
   */
  public armWarmCapture(nowMs: number = Date.now()): void {
    const needsCapture = this.warmArmedAtMs == null;
    this.hostJoinable = true;
    if (needsCapture) {
      this.warmArmedAtMs = nowMs;
      this.events.warmthArmed.emit({ timestamp: nowMs });
    }
    this.events.joinableChanged.emit({ joinable: true, timestamp: nowMs });
  }

  /**
   * Clear joinability and the settle clock, notifying listeners with the supplied
   * Date.now()-domain timestamp. Request capture release if joinability or warmth
   * was previously armed, including while a stream is active.
   */
  public leaveJoinable(nowMs: number = Date.now()): void {
    const hadWarmCapture = this.hostJoinable || this.warmArmedAtMs != null;
    this.hostJoinable = false;
    this.warmArmedAtMs = null;
    this.events.joinableChanged.emit({ joinable: false, timestamp: nowMs });
    if (hadWarmCapture) {
      this.events.captureReleaseRequested.emit({ timestamp: nowMs });
    }
  }

  public isHostJoinable(): boolean {
    return this.hostJoinable;
  }

  /** Null until warmth is armed. New peers at or after this instant skip startup burst. */
  public getPipelineSettledAtMs(): number | null {
    if (this.warmArmedAtMs == null) return null;
    return pipelineSettledAtMs(this.warmArmedAtMs);
  }

  private noteWarmArmed(nowMs: number): void {
    if (this.warmArmedAtMs == null) this.warmArmedAtMs = nowMs;
  }

  private emitStreamStopped(reason?: string): void {
    const retainCapture = captureStopDecision({
      hostJoinable: this.hostJoinable,
      reason,
    }) === 'retain';
    if (!retainCapture) this.warmArmedAtMs = null;
    this.events.streamStopped.emit({
      reason,
      timestamp: Date.now(),
      retainCapture,
    });
  }

  /**
   * Reset stream state without emitting events
   *
   * Shared cleanup logic used by both stopStream() and the peer connection timeout.
   * Closes the WebRTC peer, sets status to idle, and clears peer/invitation IDs.
   *
   * Does NOT emit streamStopped — callers decide which event to emit:
   * - stopStream() emits streamStopped (normal stream end)
   * - peer timeout emits peerConnectionTimeout (no client ever connected)
   */
  private resetStreamState(reason?: string): void {
    // Clear stream-specific fields before publishing idle so the immediate
    // status-edge flush cannot carry stale active-session data.
    this.updateHostPeerId(undefined);
    this.updateRemoteHostPeerId(undefined);
    this.updatePlayerCount(0);

    // Close the peer connection
    const existingPeer = this.peerManager.getHostPeer();
    if (existingPeer) {
      console.log('[SessionCoordinator] Closing host peer:', reason);
      this.peerManager.closeHostPeer(reason);
    } else {
      console.log('[SessionCoordinator] No active peer to close');
    }

    // Update status to idle
    this.updateStatus('idle');

    // Clear invitation ID
    this.currentInvitationId = undefined;
    this.currentCampaignId = undefined;

    // Reset session timing and tracking
    this.streamStartedAt = 0;
    this.sessionStartedAt = 0;
    this.hasActiveSession = false;
  }

  /**
   * Wait for peer ID from PeerCoordinator
   */
  private async waitForPeerId(timeoutMs: number): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout waiting for peer ID'));
      }, timeoutMs);

      // Check if we already have a peer ID
      if (this.peerCoordinator) {
        const currentPeerId = this.peerCoordinator.getHostPeerId();
        if (currentPeerId) {
          clearTimeout(timeout);
          return resolve(currentPeerId);
        }

        // Listen for peer ID assignment
        // NOTE: The event emits the peer ID string directly, not an object
        const unsubscribe = this.peerCoordinator.events.hostPeerIdChanged.addListener((peerId) => {
          if (peerId) {
            clearTimeout(timeout);
            unsubscribe();
            resolve(peerId);
          }
        });
      } else {
        clearTimeout(timeout);
        reject(new Error('PeerCoordinator not available'));
      }
    });
  }

  /**
   * Notify that stream has started
   *
   * Called by legacy bridge when startStream() completes successfully.
   * Sets status to 'hosting' and emits streamStarted event.
   *
   * TODO-ARCHITECTURE: This method should not be public. It's currently called by
   * legacy code to notify the coordinator of stream state changes. In the future,
   * this should be handled internally by a StreamManager that directly manages
   * the stream lifecycle and updates coordinator state automatically.
   * For tests, we should use mock managers instead of calling public `onXxx` methods.
   *
   * @param hostPeerId - The peer ID assigned by the API
   */
  public onStreamStarted(hostPeerId: string): void {
    console.log('[SessionCoordinator] Stream started:', hostPeerId);

    // Update status to hosting
    this.updateStatus('hosting');

    this.noteWarmArmed(Date.now());
    this.events.streamStarted.emit({
      hostPeerId,
      timestamp: Date.now(),
    });
  }

  /**
   * Notify that stream has stopped
   *
   * Called by legacy bridge when stopStream() completes.
   * Sets status to 'idle', clears peer IDs, and emits streamStopped with capture
   * retention determined by joinability and the stop reason.
   *
   * TODO-ARCHITECTURE: This method should not be public. It's currently called by
   * legacy code to notify the coordinator of stream state changes. In the future,
   * this should be handled internally by a StreamManager that directly manages
   * the stream lifecycle and updates coordinator state automatically.
   * For tests, we should use mock managers instead of calling public `onXxx` methods.
   *
   * @param reason - Optional reason for stopping (e.g., 'user_requested', 'error')
   */
  public onStreamStopped(reason?: string): void {
    console.log('[SessionCoordinator] Stream stopped:', reason);

    // Update status to idle
    this.updateStatus('idle');

    // Clear peer IDs (stream is no longer active)
    this.updateHostPeerId(undefined);
    this.updateRemoteHostPeerId(undefined);

    this.emitStreamStopped(reason);
  }

  /**
   * Update admin session presence identity used by force-recovery / orphan clear.
   */
  public setAdminSessionActive(
    active: boolean,
    identity?: { sessionId?: string; peerId?: string },
  ): void {
    this.presenceStore.updateState({
      ...this.presenceStore.currentState,
      adminSessionActive: active,
      adminSessionId: active ? identity?.sessionId : undefined,
      adminPeerId: active ? identity?.peerId : undefined,
      lastUpdated: Date.now(),
    });
  }

  /**
   * Clear hosting presence after an admin session ends.
   * Stops the broadcast stream when active so the machine is no longer reported as hosting.
   */
  public removeHostingStatus(): void {
    const status = this.presenceStore.currentState.status;
    // Both 'hosting' and 'busy' are active stream lifecycle states that need the
    // full stopStream teardown (pending peer setup, IDs, timeouts). Only fall
    // through to the lighter onStreamStopped when there is no active stream.
    if (status === 'hosting' || status === 'busy') {
      this.stopStream('admin_session_end');
      return;
    }
    this.onStreamStopped('admin_session_end');
  }

  /**
   * Cleanup (cancel pending operations)
   */
  public destroy(): void {
    console.log('[SessionCoordinator] Destroying coordinator...');
    this.unregisterHostAuthSuccess?.();
    this.unregisterHostAuthSuccess = null;
    this.managers.presence.destroy();
    console.log('[SessionCoordinator] Destroyed');
  }
}
